import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { X, Users, AlertTriangle, Loader2, Search } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEmployees, useDepartments } from '@/api/queries/employee.queries';
import { employeeApi } from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { cn } from '@/lib/cn';

const sel = 'h-8 px-2 text-[12px] bg-[var(--bg-muted)] border border-[var(--border)] rounded-[7px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] cursor-pointer w-full';
const inp = 'h-8 px-2 text-[12px] bg-[var(--bg-muted)] border border-[var(--border)] rounded-[7px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] w-full';

const EMP_TYPE_OPTIONS = [
  { value: '', label: '— unchanged —' },
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'DAILY_WAGE', label: 'Daily Wage' },
];

const STATUS_OPTIONS = [
  { value: '', label: '— unchanged —' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export function BulkEditModal({ initialSelectedIds = null, onClose }) {
  const qc = useQueryClient();
  const { data: empResp } = useEmployees({ limit: 500 });
  const { data: deptsResp } = useDepartments();
  const allEmployees = empResp?.data || [];
  const departments = deptsResp?.data || [];

  const [filters, setFilters] = useState({
    search: '',
    dept: '',
    status: '',
    type: '',
  });

  // changes: { [empId]: { department_id?, designation?, employment_type?, status? } }
  const [changes, setChanges] = useState({});
  // errors: { [empId]: string }
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(null); // { done, total }
  const [confirmClose, setConfirmClose] = useState(false);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const setCellValue = (empId, field, value) => {
    setChanges(prev => {
      const existing = prev[empId] || {};
      const next = { ...existing, [field]: value };
      // If all fields are empty/blank, remove the row from changes
      const hasAny = Object.values(next).some(v => v !== '' && v !== null && v !== undefined);
      if (!hasAny) {
        const { [empId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [empId]: next };
    });
    // Clear error for this row if user is editing
    setErrors(e => { const { [empId]: _, ...rest } = e; return rest; });
  };

  const getCellValue = (empId, field, fallback = '') => {
    return changes[empId]?.[field] ?? fallback;
  };

  const isDirty = (empId) => !!changes[empId] && Object.keys(changes[empId]).length > 0;
  const dirtyCount = Object.keys(changes).length;

  const filtered = useMemo(() => {
    let rows = [...allEmployees];
    const q = filters.search.toLowerCase();
    if (q) rows = rows.filter(e =>
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.employee_code || '').toLowerCase().includes(q)
    );
    if (filters.dept) rows = rows.filter(e => (e.department?.id || e.department_id) === filters.dept);
    if (filters.status) rows = rows.filter(e => e.status === filters.status);
    if (filters.type) rows = rows.filter(e => e.employment_type === filters.type);

    // If opened from selection, highlight only those pre-selected (but show all)
    return rows;
  }, [allEmployees, filters]);

  const isPreSelected = (id) => initialSelectedIds ? initialSelectedIds.has(id) : false;

  const handleSave = async () => {
    if (dirtyCount === 0) { toast.error('No changes to save'); return; }
    setSaving(true);
    setSaveProgress({ done: 0, total: dirtyCount });

    const empIds = Object.keys(changes);
    let successCount = 0;
    let failCount = 0;
    const newErrors = {};

    for (let i = 0; i < empIds.length; i++) {
      const id = empIds[i];
      const payload = {};
      const ch = changes[id];

      if (ch.department_id !== undefined && ch.department_id !== '') payload.department_id = ch.department_id;
      if (ch.designation !== undefined && ch.designation !== '') payload.designation = ch.designation;
      if (ch.employment_type) payload.employment_type = ch.employment_type;
      if (ch.status) payload.status = ch.status;

      try {
        await employeeApi.patch(`/v1/employees/${id}`, payload);
        successCount++;
      } catch (err) {
        failCount++;
        newErrors[id] = err?.response?.data?.error?.message || 'Update failed';
      }

      setSaveProgress({ done: i + 1, total: dirtyCount });
    }

    setSaving(false);
    setSaveProgress(null);

    if (failCount === 0) {
      toast.success(`${successCount} employee${successCount !== 1 ? 's' : ''} updated`);
      qc.invalidateQueries({ queryKey: ['employees'] });
      onClose();
    } else {
      setErrors(newErrors);
      if (successCount > 0) toast.warning(`${successCount} updated, ${failCount} failed — see highlighted rows`);
      else toast.error(`All ${failCount} updates failed`);
    }
  };

  const handleCloseRequest = () => {
    if (dirtyCount > 0) { setConfirmClose(true); return; }
    onClose();
  };

  const hasFilters = filters.search || filters.dept || filters.status || filters.type;
  const clearFilters = () => setFilters({ search: '', dept: '', status: '', type: '' });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      onClick={e => e.target === e.currentTarget && handleCloseRequest()}>
      <div className="bg-[var(--bg-card)] rounded-t-[20px] sm:rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-5xl border border-[var(--border)] flex flex-col h-[92vh] sm:h-[90vh] fade-in">

        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-[var(--border)] shrink-0">
          <div className="w-8 h-8 rounded-[8px] bg-[var(--accent)]/10 flex items-center justify-center shrink-0">
            <Users size={15} className="text-[var(--accent)]"/>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-[var(--text-primary)]">Bulk Edit Employees</h2>
            <p className="text-[11px] text-[var(--text-tertiary)]">
              {dirtyCount > 0
                ? `${dirtyCount} row${dirtyCount !== 1 ? 's' : ''} edited — click "Apply Changes" to save`
                : 'Edit any cell to track a change. Changes save only when you click Apply.'}
            </p>
          </div>
          <button onClick={handleCloseRequest} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)] shrink-0">
            <X size={15}/>
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-[var(--border)] shrink-0 bg-[var(--bg-muted)]">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"/>
            <input value={filters.search} onChange={e => setFilter('search', e.target.value)}
              placeholder="Search by name or code…"
              className="h-8 pl-7 pr-3 text-[12px] bg-[var(--bg-card)] border border-[var(--border)] rounded-[7px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] w-48"/>
          </div>
          <select value={filters.dept} onChange={e => setFilter('dept', e.target.value)}
            className="h-8 px-2 text-[12px] bg-[var(--bg-card)] border border-[var(--border)] rounded-[7px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]">
            <option value="">All Departments</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
            className="h-8 px-2 text-[12px] bg-[var(--bg-card)] border border-[var(--border)] rounded-[7px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]">
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ON_LEAVE">On Leave</option>
            <option value="TERMINATED">Terminated</option>
          </select>
          <select value={filters.type} onChange={e => setFilter('type', e.target.value)}
            className="h-8 px-2 text-[12px] bg-[var(--bg-card)] border border-[var(--border)] rounded-[7px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]">
            <option value="">All Types</option>
            <option value="FULL_TIME">Full Time</option>
            <option value="PART_TIME">Part Time</option>
            <option value="CONTRACT">Contract</option>
            <option value="DAILY_WAGE">Daily Wage</option>
          </select>
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 text-[11px] text-[var(--text-tertiary)] hover:text-[var(--danger)]">
              <X size={10}/> Clear
            </button>
          )}
          <span className="ml-auto text-[11px] text-[var(--text-tertiary)]">
            {filtered.length} employee{filtered.length !== 1 ? 's' : ''}
            {initialSelectedIds && initialSelectedIds.size > 0 && ` · ${initialSelectedIds.size} pre-selected`}
          </span>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full min-w-[760px]">
            <thead className="sticky top-0 bg-[var(--bg-muted)] z-10">
              <tr className="border-b border-[var(--border)]">
                <th className="text-left text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-4 py-2.5 w-[200px]">Employee</th>
                <th className="text-left text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-3 py-2.5 w-[150px]">Department</th>
                <th className="text-left text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-3 py-2.5 w-[150px]">Designation</th>
                <th className="text-left text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-3 py-2.5 w-[130px]">Type</th>
                <th className="text-left text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-3 py-2.5 w-[110px]">Status</th>
                <th className="w-8"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filtered.map(e => {
                const dirty = isDirty(e.id);
                const rowError = errors[e.id];
                const preSelected = isPreSelected(e.id);
                const warnDailyWage = getCellValue(e.id, 'employment_type') === 'DAILY_WAGE';
                return (
                  <tr key={e.id}
                    className={cn('transition-colors',
                      rowError ? 'bg-red-50 dark:bg-red-950/20' :
                      dirty ? 'bg-amber-50/60 dark:bg-amber-950/20' :
                      preSelected ? 'bg-[var(--accent)]/5' :
                      'hover:bg-[var(--bg-muted)]'
                    )}>
                    {/* Employee column */}
                    <td className={cn('px-4 py-2.5 relative', dirty && 'pl-6')}>
                      {dirty && <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 rounded-r"/>}
                      <div className="flex items-center gap-2.5">
                        <Avatar name={`${e.first_name} ${e.last_name}`} src={e.photo_url} size="sm"/>
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold text-[var(--text-primary)] truncate">{e.first_name} {e.last_name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)] truncate">{e.employee_code}</p>
                        </div>
                      </div>
                      {rowError && (
                        <p className="text-[10px] text-red-600 dark:text-red-400 mt-0.5 flex items-center gap-1">
                          <AlertTriangle size={9}/> {rowError}
                        </p>
                      )}
                    </td>

                    {/* Department */}
                    <td className="px-3 py-2.5">
                      <select
                        value={getCellValue(e.id, 'department_id', e.department?.id || e.department_id || '')}
                        onChange={ev => setCellValue(e.id, 'department_id', ev.target.value)}
                        className={cn(sel, getCellValue(e.id, 'department_id') !== (e.department?.id || e.department_id || '') && getCellValue(e.id, 'department_id') !== '' && 'border-amber-400 bg-amber-50 dark:bg-amber-950/30')}>
                        <option value="">— unchanged —</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </td>

                    {/* Designation */}
                    <td className="px-3 py-2.5">
                      <input
                        value={getCellValue(e.id, 'designation', e.designation || '')}
                        onChange={ev => setCellValue(e.id, 'designation', ev.target.value)}
                        placeholder={e.designation || 'e.g. Engineer'}
                        className={cn(inp, getCellValue(e.id, 'designation') !== (e.designation || '') && getCellValue(e.id, 'designation') !== '' && 'border-amber-400 bg-amber-50 dark:bg-amber-950/30')}
                      />
                    </td>

                    {/* Employment Type */}
                    <td className="px-3 py-2.5">
                      <div>
                        <select
                          value={getCellValue(e.id, 'employment_type', '')}
                          onChange={ev => setCellValue(e.id, 'employment_type', ev.target.value)}
                          className={cn(sel, getCellValue(e.id, 'employment_type') && 'border-amber-400 bg-amber-50 dark:bg-amber-950/30')}>
                          {EMP_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        {warnDailyWage && (
                          <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                            <AlertTriangle size={8}/> Clears salary structure
                          </p>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-3 py-2.5">
                      <select
                        value={getCellValue(e.id, 'status', '')}
                        onChange={ev => setCellValue(e.id, 'status', ev.target.value)}
                        className={cn(sel, getCellValue(e.id, 'status') && 'border-amber-400 bg-amber-50 dark:bg-amber-950/30')}>
                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>

                    {/* Clear row changes */}
                    <td className="pr-3">
                      {dirty && (
                        <button onClick={() => setChanges(p => { const { [e.id]: _, ...r } = p; return r; })}
                          className="p-1 rounded hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-[var(--danger)]" title="Clear row changes">
                          <X size={12}/>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">
                    {allEmployees.length === 0 ? 'Loading employees…' : 'No employees match the current filters'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Progress bar */}
        {saveProgress && (
          <div className="px-5 py-2.5 border-t border-[var(--border)] bg-[var(--bg-muted)] shrink-0">
            <div className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)]">
              <Loader2 size={12} className="animate-spin shrink-0"/>
              Saving {saveProgress.done} of {saveProgress.total}…
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
              <div className="h-full bg-[var(--accent)] rounded-full transition-all" style={{ width: `${(saveProgress.done / saveProgress.total) * 100}%` }}/>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-[var(--border)] shrink-0">
          {dirtyCount > 0 && (
            <p className="text-[12px] text-amber-600 dark:text-amber-400 flex items-center gap-1.5 mr-auto">
              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block"/>
              {dirtyCount} row{dirtyCount !== 1 ? 's' : ''} with pending changes
            </p>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={handleCloseRequest}>Cancel</Button>
            <Button onClick={handleSave} disabled={dirtyCount === 0 || saving}>
              {saving ? <Loader2 size={14} className="animate-spin"/> : null}
              Apply {dirtyCount > 0 ? `${dirtyCount} ` : ''}Change{dirtyCount !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>

      {/* Unsaved changes confirmation dialog */}
      {confirmClose && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-[var(--bg-card)] rounded-[16px] border border-[var(--border)] shadow-[var(--shadow-xl)] p-5 w-full max-w-sm fade-in">
            <p className="text-[15px] font-bold text-[var(--text-primary)] mb-1.5">Discard changes?</p>
            <p className="text-[13px] text-[var(--text-secondary)] mb-4">
              You have {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}. They will be lost if you close now.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmClose(false)} className="flex-1">Keep Editing</Button>
              <Button variant="destructive" onClick={onClose} className="flex-1">Discard</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
